const WEEKS_PER_GOAL = 4
const GOAL_CYCLE = 4
const OUTCOME_CHIPS_PER_GOAL = 3

export const MILESTONE_PLACEHOLDER_ICON =
  '/images/journey/milestone-placeholder.svg'

// Plan-specific milestone tile sets. The set name doubles as the asset
// filename prefix; maxGoal limits sets that ship fewer goal variants.
const MILESTONE_ICON_SETS = {
  default: { prefix: '', maxGoal: GOAL_CYCLE },
  neurokids: { prefix: 'neurokids-', maxGoal: 3 },
} as const

export type MilestoneIconSet = keyof typeof MILESTONE_ICON_SETS

// Which tile set a plan uses, by plan code.
export function milestoneIconSetForPlan(
  planCode: string | null | undefined,
): MilestoneIconSet {
  if (planCode?.includes('neuroKids') || planCode?.includes('kdsBody'))
    return 'neurokids'
  return 'default'
}

function withinGoalWeek(weekNumber: number): number {
  return ((weekNumber - 1) % WEEKS_PER_GOAL) + 1
}

function goalAssetNumber(goalNumber: number): number {
  return (Math.max(0, goalNumber - 1) % GOAL_CYCLE) + 1
}

export function milestoneTileIcon(
  goalNumber: number,
  weekNumber: number,
  iconSet: MilestoneIconSet = 'default',
): string | null {
  const goal = goalAssetNumber(goalNumber)
  const week = withinGoalWeek(weekNumber)
  const set = MILESTONE_ICON_SETS[iconSet]
  if (goal > set.maxGoal) return null
  return `/images/journey/milestones/${set.prefix}goal${goal}-week${week}.svg`
}

// Per-goal milestone-celebration illustration (raster). Goal asset cycles 1-4.
export function milestoneIllustration(goalNumber: number): string {
  const goal = goalAssetNumber(goalNumber)
  return `/images/journey/illustrations/goal${goal}-milestone.png`
}

// Per-goal outcome chip icon: goal asset cycles 1-4, up to 3 chips per goal.
export function outcomeChipIcon(
  goalNumber: number,
  chipIndex: number,
): string | null {
  if (chipIndex < 0 || chipIndex >= OUTCOME_CHIPS_PER_GOAL) return null
  const goal = goalAssetNumber(goalNumber)
  return `/images/journey/outcomes/goal${goal}-outcome${chipIndex + 1}.svg`
}

// [top, right, bottom, left] insets of a glyph inside its 54px tile.
type GlyphInsets = [number, number, number, number]

const SECTION_GLYPH_INSETS: GlyphInsets[] = [
  [8, 8, 8, 8],
  [10, 10, 10, 10],
  [10, 10, 10, 10],
  [10, 4, 10, 4],
]

// Kids art is unique per (goal, milestone); Figma's 72px tile scaled ×0.75.
const KIDS_GLYPH_INSETS: GlyphInsets[][] = [
  [
    [10.5, 14.25, 13.5, 14.25],
    [11.25, 12.75, 14.25, 12],
    [11.25, 12.75, 14.25, 13.5],
    [8.25, 10.5, 10.5, 12],
  ],
  [
    [12.75, 12.75, 12.75, 12.75],
    [14, 10, 14, 10],
    [14.25, 12, 14.25, 12],
    [9.75, 12.75, 9.75, 12.75],
  ],
  [
    [10.5, 11.25, 11.25, 11.25],
    [7.5, 14, 14.25, 8.75],
    [13.5, 10.5, 12.75, 10.5],
    [12.75, 8, 12.25, 11.25],
  ],
]

// The step arm's glyph cycles on the section, the same ordinal its tint cycles on.
export function sectionMilestoneGlyph(
  sectionNumber: number,
  goalNumber: number,
  iconSet: MilestoneIconSet,
): { src: string; insets: GlyphInsets } {
  const icon = ((Math.max(sectionNumber, 1) - 1) % 4) + 1
  if (iconSet === 'neurokids') {
    const goal = Math.min(Math.max(goalNumber, 1), 3)
    return {
      src: `/images/journey/milestone-glyphs/kds-goal${goal}-m${icon}.svg`,
      insets: KIDS_GLYPH_INSETS[goal - 1][icon - 1],
    }
  }
  return {
    src: `/images/journey/milestone-glyphs/milestone${icon}.svg`,
    insets: SECTION_GLYPH_INSETS[icon - 1],
  }
}
